import React from 'react';
import { Typography, Tag, Row, Col, Tooltip } from 'antd';
import { getHealthScoreColor } from '../utils/healthColorUtils';

const { Text } = Typography;

/**
 * Compact Parameter Visualization Component
 * Designed for space-efficient display of multiple parameters in modals
 * Maintains all functionality while reducing vertical space usage
 */
const CompactParameterVisualization = ({
  zScore,
  currentValue,
  idealTarget,
  unit = '',
  parameterName,
  priority = 'Normal',
  specificImpact = 0,
  category = '',
  hasData = true
}) => {
  // Helper function to safely extract value from potential object
  const getDisplayValue = (value) => {
    if (value && typeof value === 'object' && value.value !== undefined) {
      return value.value;
    }
    return value;
  };

  // Extract safe values for display
  const safeCurrentValue = getDisplayValue(currentValue);
  const safeIdealTarget = getDisplayValue(idealTarget);
  // Color mapping for priority levels
  const priorityColors = {
    High: '#ff4d4f',
    Moderate: '#fa8c16', 
    Low: '#1890ff',
    Normal: '#52c41a',
    'No Data': '#d9d9d9'
  };

  // Z-score visualization using health-based color gradient
  const getHealthScoreFromZScore = (z) => {
    const absZ = Math.abs(z);
    if (absZ <= 1) return 85; // Good health
    if (absZ <= 2) return 65; // Fair health
    if (absZ <= 3) return 45; // Poor health
    return 25; // Critical health
  };

  const getZScoreColor = (z) => {
    const healthScore = getHealthScoreFromZScore(z);
    return getHealthScoreColor(healthScore);
  };

  const getZScorePosition = (z) => {
    const minZ = -6;
    const maxZ = 6;
    const clampedZ = Math.max(minZ, Math.min(maxZ, z));
    return ((clampedZ - minZ) / (maxZ - minZ)) * 100;
  };

  const priorityColor = priorityColors[priority] || priorityColors.Normal;
  const zScoreColor = hasData ? getZScoreColor(zScore) : priorityColors['No Data'];
  const deviationText = hasData ? (zScore > 0 ? 'above target' : 'below target') : 'no data';

  if (!hasData) {
    return (
      <div style={{
        padding: '8px 12px',
        background: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #e9ecef',
        marginBottom: '4px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: '13px', color: '#495057' }}>
              {parameterName}
            </Text>
            {category && (
              <Tag size="small" style={{ marginLeft: '8px', fontSize: '10px' }}>
                {category}
              </Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            No data available
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '8px 12px',
      background: '#ffffff',
      borderRadius: '6px',
      border: '1px solid #e8e8e8',
      marginBottom: '4px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
    }}>
      <Row align="middle" gutter={16}>
        {/* Parameter Name */}
        <Col flex="auto">
          <Text strong style={{ fontSize: '13px', color: '#2c3e50' }}>
            {parameterName}
          </Text>
          {category && (
            <Tag size="small" style={{ marginLeft: '8px', fontSize: '10px' }}>
              {category}
            </Tag>
          )}
        </Col>

        {/* Metrics Columns */}
        <Col style={{width: '240px'}}>
          <Row gutter={12}>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                Current
              </Text>
              <Text strong style={{ fontSize: '11px' }}>
                {typeof safeCurrentValue === 'object' ? JSON.stringify(safeCurrentValue) : safeCurrentValue}{unit}
              </Text>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                Target
              </Text>
              <Text strong style={{ fontSize: '11px' }}>
                {typeof safeIdealTarget === 'object' ? JSON.stringify(safeIdealTarget) : safeIdealTarget}{unit}
              </Text>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                Z-Score
              </Text>
              <Text strong style={{ color: zScoreColor, fontSize: '11px' }}>
                {Math.abs(zScore)?.toFixed(1)}σ
              </Text>
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                Priority Score
              </Text>
              <Text strong style={{ color: priorityColor, fontSize: '11px' }}>
                {specificImpact?.toFixed(2)}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Compact Z-score visualization bar */}
      <div style={{
        position: 'relative',
        height: '8px',
        background: `linear-gradient(to right,
          ${getHealthScoreColor(25)},
          ${getHealthScoreColor(45)} 25%,
          ${getHealthScoreColor(85)} 45%,
          ${getHealthScoreColor(85)} 55%,
          ${getHealthScoreColor(45)} 75%,
          ${getHealthScoreColor(25)})`,
        borderRadius: '4px',
        border: '1px solid #e8e8e8',
        marginTop: '8px'
      }}>
        {/* Ideal target marker (center) */}
        <Tooltip
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Ideal Target</div>
              {safeIdealTarget !== null && safeIdealTarget !== undefined ? (
                <div>{typeof safeIdealTarget === 'object' ? JSON.stringify(safeIdealTarget) : safeIdealTarget} {unit}</div>
              ) : (
                <div>Target value not available</div>
              )}
            </div>
          }
          placement="top"
        >
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: '0',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            cursor: 'help'
          }}>
            <div style={{
              height: '8px',
              width: '2px',
              backgroundColor: '#2f54eb',
              borderRadius: '1px'
            }}></div>
          </div>
        </Tooltip>

        {/* Current value marker */}
        <Tooltip
          title={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Current Value</div>
              {safeCurrentValue !== null && safeCurrentValue !== undefined ? (
                <div>{typeof safeCurrentValue === 'object' ? JSON.stringify(safeCurrentValue) : safeCurrentValue} {unit}</div>
              ) : (
                <div>Current value not available</div>
              )}
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#888' }}>
                Deviation: {Math.abs(zScore).toFixed(2)}σ {zScore > 0 ? 'above' : 'below'} target
              </div>
            </div>
          }
          placement="top"
        >
          <div style={{
            position: 'absolute',
            left: `${Math.max(2, Math.min(98, getZScorePosition(zScore)))}%`,
            transform: 'translateX(-50%)',
            top: '0',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            cursor: 'help'
          }}>
            <div style={{
              height: '8px',
              width: '3px',
              backgroundColor: zScoreColor,
              borderRadius: '2px',
              boxShadow: `0 1px 2px ${zScoreColor}40`
            }}></div>
          </div>
        </Tooltip>
      </div>

      {/* Compact status indicator */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: '4px'
      }}>
        <Text type="secondary" style={{ fontSize: '10px' }}>
          {deviationText}
        </Text>
        <Tag 
          color={priority === 'High' ? 'red' : priority === 'Moderate' ? 'orange' : priority === 'Low' ? 'blue' : 'green'}
          style={{ fontSize: '9px', padding: '0 4px', lineHeight: '16px' }}
        >
          {priority}
        </Tag>
      </div>
    </div>
  );
};

export default CompactParameterVisualization;
